import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { TableEmptyState } from './TableEmptyState';
import type { ResponsiveTableColumn } from './types';

interface ResponsiveTableCardProps<T> {
  data: readonly T[];
  columns: ResponsiveTableColumn<T>[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  renderCardHeader?: (row: T) => ReactNode;
  emptyState?: ReactNode;
  ariaLabel?: string;
  className?: string;
  rowClassName?: (row: T) => string | undefined;
  fixedPageSize?: number;
}

/**
 * Layout mobile (< md): ogni riga è una card stack verticale.
 *
 * Struttura card:
 *   ┌────────────────────────────────────────────────────┐
 *   │ [primary]  Nome candidato · email (heading)        │ ← renderCardHeader OR primary cols
 *   │            Position · Team · Channel (subtitle)    │
 *   │ ─────────────────────────────────────────────────  │
 *   │ Label1: value1   ·   Label2: value2 (secondary)    │
 *   │ Label3: value3                                     │
 *   │ ─────────────────────────────────────────────────  │
 *   │ [tertiary badges/inline]                           │
 *   └────────────────────────────────────────────────────┘
 *
 * Le colonne `priority: 'hidden'` (es. checkbox row select, menu actions)
 * non vengono renderizzate qui — gestite separatamente dal parent se servono.
 */
export function ResponsiveTableCard<T>({
  data,
  columns,
  getRowKey,
  onRowClick,
  renderCardHeader,
  emptyState,
  ariaLabel,
  className,
  rowClassName,
  fixedPageSize,
}: ResponsiveTableCardProps<T>) {
  if (data.length === 0) {
    return (
      <div className={cn('rounded-lg border border-border bg-card p-6 text-center', className)}>
        {emptyState ?? <TableEmptyState />}
      </div>
    );
  }

  const primary = columns.filter((c) => c.priority === 'primary');
  const secondary = columns.filter((c) => c.priority === 'secondary');
  const tertiary = columns.filter((c) => c.priority === 'tertiary');
  const spacerCount = fixedPageSize ? Math.max(0, fixedPageSize - data.length) : 0;

  return (
    <ul
      role="list"
      aria-label={ariaLabel}
      className={cn('flex flex-col gap-2.5', className)}
    >
      {data.map((row) => {
        const headerNode = renderCardHeader
          ? renderCardHeader(row)
          : primary.length > 0 && (
              <div className="space-y-0.5">
                {primary.map((col) => (
                  <div key={col.id} className={col === primary[0] ? 'font-semibold text-foreground text-[15px] leading-tight' : 'text-xs text-muted-foreground'}>
                    {col.renderMobile ? col.renderMobile(row) : col.render(row, { isMobile: true })}
                  </div>
                ))}
              </div>
            );

        return (
          <li
            key={getRowKey(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn(
              'rounded-lg border border-border bg-card p-3.5 shadow-sm transition-colors',
              onRowClick && 'cursor-pointer hover:bg-accent active:bg-accent/80',
              rowClassName?.(row),
            )}
          >
            {headerNode}

            {secondary.length > 0 && (
              <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                {secondary.map((col) => (
                  <div key={col.id} className="min-w-0">
                    <dt className="text-muted-foreground uppercase tracking-wide text-[10px] mb-0.5">
                      {col.label}
                    </dt>
                    <dd className={cn('text-foreground', col.truncate !== false && 'truncate')}>
                      {col.renderMobile ? col.renderMobile(row) : col.render(row, { isMobile: true })}
                    </dd>
                  </div>
                ))}
              </dl>
            )}

            {tertiary.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {tertiary.map((col) => (
                  <div key={col.id}>
                    {col.renderMobile ? col.renderMobile(row) : col.render(row, { isMobile: true })}
                  </div>
                ))}
              </div>
            )}
          </li>
        );
      })}
      {Array.from({ length: spacerCount }, (_, i) => (
        <li
          key={`__card-spacer-${i}`}
          className="min-h-[88px] pointer-events-none invisible"
          aria-hidden
        />
      ))}
    </ul>
  );
}
